// Expiry Management Service
// Handles dynamic expiry settings, monitoring, and notifications

export const DEFAULT_EXPIRY_SETTINGS = {
  notificationIntervals: [7, 14, 30, 60],
  enableAutoNotifications: true,
  recipientRoles: [],
  skipExpiredContracts: false,
};

// Fetch dynamic expiry settings from SharePoint
export const getExpirySettings = async (service) => {
  try {
    const result = await service.getListItems("ExpirySettings");
    if (!result?.value?.length) {
      // Create default settings if none exist
      await service.createListItem("ExpirySettings", {
        Title: "Global Expiry Notifications",
        NotificationIntervals: JSON.stringify(DEFAULT_EXPIRY_SETTINGS.notificationIntervals),
        EnableAutoNotifications: true,
        RecipientRoles: JSON.stringify(DEFAULT_EXPIRY_SETTINGS.recipientRoles),
      });
      return DEFAULT_EXPIRY_SETTINGS;
    }

    const settings = result.value[0].fields;
    return {
      notificationIntervals: JSON.parse(settings.NotificationIntervals || JSON.stringify(DEFAULT_EXPIRY_SETTINGS.notificationIntervals)),
      enableAutoNotifications: settings.EnableAutoNotifications !== false,
      recipientRoles: JSON.parse(settings.RecipientRoles || JSON.stringify(DEFAULT_EXPIRY_SETTINGS.recipientRoles)),
      skipExpiredContracts: settings.SkipExpiredContracts === true,
      lastCheck: settings.LastNotificationCheck,
      settingsId: result.value[0].id,
    };
  } catch (e) {
    console.error("Error fetching expiry settings:", e);
    return DEFAULT_EXPIRY_SETTINGS;
  }
};

// Update expiry settings
export const updateExpirySettings = async (service, settingsId, updates) => {
  try {
    // Ensure arrays are stored as JSON strings
    const payload = {
      ...updates,
      NotificationIntervals: typeof updates.NotificationIntervals === 'string'
        ? updates.NotificationIntervals
        : JSON.stringify(updates.NotificationIntervals),
      RecipientRoles: typeof updates.RecipientRoles === 'string'
        ? updates.RecipientRoles
        : JSON.stringify(updates.RecipientRoles),
      LastNotificationCheck: new Date().toISOString(),
    };

    await service.updateListItem("ExpirySettings", settingsId, payload);
    return true;
  } catch (e) {
    console.error("Error updating expiry settings:", e);
    return false;
  }
};

// Get all contracts approaching expiry with detailed info
export const getExpiringContracts = async (service, settings) => {
  try {
    const contracts = await service.getListItems("Contracts");
    if (!contracts?.value?.length) return [];

    const now = new Date();
    const expiringList = [];

    for (const item of contracts.value) {
      const contract = { ...item.fields, id: item.id };

      // Skip contracts that don't qualify for expiry checks
      if (!contract.ExpiryDate || contract.Status === "Expired" || contract.Status === "Cancelled" || contract.Status === "Executed") {
        continue;
      }

      // Only check renewable contracts
      if (contract.IsRenewal !== "Yes") {
        continue;
      }

      const expiryDate = new Date(contract.ExpiryDate);
      const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

      // Check if it matches any notification interval
      const shouldNotify = settings.notificationIntervals.some(interval => Math.abs(daysRemaining - interval) <= 1);

      if (shouldNotify || daysRemaining <= 0) {
        expiringList.push({
          ...contract,
          daysRemaining,
          isExpired: daysRemaining <= 0,
          urgencyLevel: daysRemaining <= 7 ? "critical" : daysRemaining <= 30 ? "urgent" : daysRemaining <= 60 ? "soon" : "upcoming",
        });
      }
    }

    // Sort by urgency (fewest days first)
    return expiringList.sort((a, b) => a.daysRemaining - b.daysRemaining);
  } catch (e) {
    console.error("Error getting expiring contracts:", e);
    return [];
  }
};

// Determine notification recipients based on settings
export const getNotificationRecipients = (contract, settings) => {
  const recipients = new Set();

  if (settings.recipientRoles.includes("admin")) {
    // Admin emails fetched separately
    recipients.add("__ADMIN__");
  }

  if (settings.recipientRoles.includes("requester") && contract.RequestedBy) {
    recipients.add(contract.RequestedBy);
  }

  if (settings.recipientRoles.includes("owner") && contract.ContractOwner) {
    recipients.add(contract.ContractOwner);
  }

  if (settings.recipientRoles.includes("categoryManager") && contract.CategoryManager) {
    recipients.add(contract.CategoryManager);
  }

  return Array.from(recipients);
};

// Log notification to audit trail
export const logNotification = async (service, notification) => {
  try {
    await service.createListItem("NotificationLog", {
      Title: `${notification.notificationType}_${notification.contractRefNo}`,
      ContractID: notification.contractId,
      ContractRefNo: notification.contractRefNo,
      NotificationType: notification.notificationType,
      DaysRemaining: notification.daysRemaining,
      RecipientEmails: Array.isArray(notification.recipientEmails)
        ? notification.recipientEmails.join(";")
        : notification.recipientEmails,
      Subject: notification.subject,
      SentAt: new Date().toISOString(),
      Status: notification.status || "sent",
      ErrorMessage: notification.errorMessage || "",
    });
    return true;
  } catch (e) {
    console.error("Error logging notification:", e);
    return false;
  }
};

// Get notification history for a contract
export const getContractNotificationHistory = async (service, contractId) => {
  try {
    const history = await service.getListItems(
      "NotificationLog",
      `fields/ContractID eq '${contractId}'`
    );
    return (history?.value || []).map(item => ({
      ...item.fields,
      id: item.id,
      sentAt: new Date(item.fields.SentAt),
    })).sort((a, b) => b.sentAt - a.sentAt);
  } catch (e) {
    console.error("Error fetching notification history:", e);
    return [];
  }
};

// Mark contract as expired (auto-called when daysRemaining <= 0)
export const markContractAsExpired = async (service, contractId) => {
  try {
    await service.updateListItem("Contracts", contractId, {
      Status: "Expired",
    });
    return true;
  } catch (e) {
    console.error("Error marking contract as expired:", e);
    return false;
  }
};

// Extend contract expiry date
export const extendContractExpiry = async (service, contractId, newExpiryDate) => {
  try {
    await service.updateListItem("Contracts", contractId, {
      ExpiryDate: new Date(newExpiryDate).toISOString().split('T')[0],
    });
    return true;
  } catch (e) {
    console.error("Error extending contract expiry:", e);
    return false;
  }
};

// Build expiry notification email HTML
export const buildExpiryEmailHTML = (contract, daysRemaining) => {
  const isOverdue = daysRemaining <= 0;
  const urgencyColor = daysRemaining <= 7 ? "#a4262c" : daysRemaining <= 30 ? "#ca5010" : "#c9a84c";
  const urgencyLabel = daysRemaining <= 0 ? "OVERDUE" : daysRemaining <= 7 ? "CRITICAL" : daysRemaining <= 30 ? "URGENT" : "UPCOMING";

  return `
    <div style="font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <div style="background: linear-gradient(90deg, ${urgencyColor}, transparent); padding: 1px 0; margin-bottom: 24px;"></div>

      <div style="padding: 0 20px;">
        <div style="font-size: 28px; font-weight: 600; color: #0a1628; margin-bottom: 8px;">
          Contract Expiry Reminder
        </div>
        <p style="color: #666; margin: 0; font-size: 14px;">
          ${isOverdue ? "This contract has EXPIRED" : `Expires in ${daysRemaining} days`}
        </p>
      </div>

      <div style="background: #f5f5f5; margin: 24px 0; padding: 16px 20px; border-left: 4px solid ${urgencyColor};">
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; font-weight: 600; color: #333; width: 120px;">Status:</td>
            <td style="padding: 8px 0; color: ${urgencyColor}; font-weight: 600; font-size: 16px;">${urgencyLabel}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: 600; color: #333;">Contract:</td>
            <td style="padding: 8px 0; color: #0078d4; font-family: Courier New, monospace;">${contract.RefNo}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: 600; color: #333;">Title:</td>
            <td style="padding: 8px 0; color: #333;">${contract.Title || "Untitled"}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: 600; color: #333;">Vendor:</td>
            <td style="padding: 8px 0; color: #666;">${contract.VendorName || "—"}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: 600; color: #333;">Expiry Date:</td>
            <td style="padding: 8px 0; color: #333; font-weight: 600;">${new Date(contract.ExpiryDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: 600; color: #333;">Days Remaining:</td>
            <td style="padding: 8px 0; color: ${urgencyColor}; font-weight: 600; font-size: 18px;">${isOverdue ? "EXPIRED" : daysRemaining + " days"}</td>
          </tr>
        </table>
      </div>

      <div style="text-align: center; margin: 24px 0;">
        <a href="${window?.location?.origin}/contracts/${contract.id}" style="display: inline-block; background: ${urgencyColor}; color: white; padding: 12px 24px; border-radius: 6px; text-decoration: none; font-weight: 600; font-size: 14px;">
          Review Contract
        </a>
      </div>

      <div style="background: #f5f5f5; padding: 16px 20px; border-radius: 6px; margin: 24px 0; font-size: 12px; color: #666;">
        <strong>Next Steps:</strong>
        <ul style="margin: 8px 0; padding-left: 20px;">
          <li>Review the contract terms and conditions</li>
          <li>Decide on renewal, extension, or termination</li>
          <li>Contact the vendor if renewal is needed</li>
          <li>Update the contract status in LexCore</li>
        </ul>
      </div>

      <hr style="border: none; border-top: 1px solid #ddd; margin: 24px 0;">
      <p style="font-size: 12px; color: #999; text-align: center; margin: 0;">
        Automated message from LexCore Contract Management System. Do not reply to this email.
      </p>
    </div>
  `;
};
